// Inline chip rendered for every viewable doc path detected in a chat
// message (D-012, sprint md-doc-viewer; widened in D-019 beyond `.md`
// to cover the full doc-viewer allowlist). Click → opens DocViewerSheet
// via the `?doc=...&agent=...` query string.
//
// Visual: subtle pill, extension-aware icon + basename. Full path on
// hover via `title`. Reuses the same accent / hover treatment as
// AttachmentChip; no new tokens.
//
// Icon picks (D-019):
//   - Image extensions (png / jpg / jpeg / webp / gif) → `Image`.
//   - Everything else (md / txt / json / pdf) → `FileText`.
//     Lucide does not ship a clean PDF icon; the `.pdf` suffix in the
//     basename carries the format signal in chat.

import { Icon } from './atoms/Icon';

const IMAGE_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.webp', '.gif'] as const;

function pickIcon(path: string): 'Image' | 'FileText' {
  const lower = path.toLowerCase();
  for (const ext of IMAGE_EXTENSIONS) {
    if (lower.endsWith(ext)) return 'Image';
  }
  return 'FileText';
}

export interface DocPathChipProps {
  path: string;
  onOpen: (path: string) => void;
}

export function DocPathChip({ path, onOpen }: DocPathChipProps) {
  const basename = path.split('/').pop() ?? path;
  const iconName = pickIcon(path);
  return (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onOpen(path);
      }}
      className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 align-baseline text-body-chat font-medium text-foreground/85 hover:bg-accent transition-colors duration-100"
      data-testid="doc-path-chip"
      data-doc-path={path}
      title={path}
    >
      <Icon name={iconName} size={12} aria-hidden />
      {basename}
    </button>
  );
}
